<script setup lang="ts">
import { useCmsTranslations } from "@shopware-pwa/composables-next";
import {
  useAddToCart,
  useCart,
  useNotifications,
  useCartNotification,
  useCartErrorParamsResolver,
} from "#imports";
import { toRefs, computed, ref, watch } from "vue";
import { defu } from "defu";
import type { Schemas } from "#shopware";
import { getCmsTranslate } from "@shopware-pwa/helpers-next";
import { addToCartTracking } from "~/components/utilities/GtmTrackings";

const { pushSuccess, pushError } = useNotifications();
const { getErrorsCodes } = useCartNotification();
const { resolveCartError } = useCartErrorParamsResolver();

const props = defineProps<{
  product: Schemas["Product"];
  bundles?: any;
}>();

const { t } = useI18n();
const { product, bundles } = toRefs(props);
const { addToCart, quantity } = useAddToCart(product);
const { addProducts } = useCart();

const isAddToCartProcessing = ref(false);

type Translations = {
  errors: {
    [key: string]: string;
  };
};

let translations: Translations = {
  errors: {
    "product-stock-reached":
      "The product {name} is only available {quantity} times",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const minPurchase = computed(() => {
  let bundleMin = 1;
  bundles.value?.forEach((item) => {
    const itemMin = item?.minPurchase || 1;
    bundleMin = Math.max(bundleMin, itemMin);
  });
  return bundleMin;
});

const maxPurchase = computed(() => {
  let bundleMax = Number.MAX_SAFE_INTEGER;
  bundles.value?.forEach((item) => {
    const itemMax = item?.calculatedMaxPurchase || 0;
    const itemStock = item?.availableStock || 0;
    const itemQuantity = item.quantity || 1;
    const itemLimit = Math.floor(itemMax / itemQuantity);
    const stockLimit = Math.floor(itemStock / itemQuantity);
    const effectiveLimit = Math.min(itemLimit, stockLimit);
    bundleMax = Math.min(bundleMax, effectiveLimit);
  });
  return bundleMax === Number.MAX_SAFE_INTEGER ? 100 : bundleMax;
});

const isAvailable = computed(() => {
  let allAvailable = true;
  bundles.value?.forEach((item) => {
    const available = item?.available;
    const stock = item?.availableStock || 0;
    if (!available || stock < (item.quantity || 1)) {
      allAvailable = false;
    }
  });
  return allAvailable;
});

const addToCartProxy = async () => {
  isAddToCartProcessing.value = true;

  try {
    const bundlePayload = bundles.value
      ? {
          isBundle: true,
          bundleProducts: bundles.value.map((item) => ({
            id: item.id,
            name: item.name
          })),
          set_product_discount_articlenumber: product.value.customFields?.set_product_discount_articlenumber ?? null
        }
      : [];

    if (!product.value?.id) throw new Error("Product id is required");

    await addProducts([
      {
        id: product.value.id,
        quantity: quantity.value,
        type: "product",
        payload: {
          bundle: bundlePayload,
        },
      },
    ]);

    const errors = getErrorsCodes();

    if (errors.length) {
      errors.forEach((element) => {
        const { messageKey, params } = resolveCartError(element);
        pushError(getCmsTranslate(translations.errors[messageKey], params));
      });
    } else {
      pushSuccess(
        `${props.product?.translated.name} ${t("productDetails.messages.addedToCart")}`
      );
      addToCartTracking(product.value, quantity.value);
      quantity.value = minPurchase.value;
    }
  } catch (error) {
    pushError(t("errors.addToCartError"));
  } finally {
    isAddToCartProcessing.value = false;
  }
};

const increaseQuantity = () => {
  if (quantity.value < maxPurchase?.value) {
    quantity.value++;
  }
};

const decreaseQuantity = () => {
  if (quantity.value > minPurchase?.value) {
    quantity.value--;
  }
};
</script>

<template>
  <div class="add-product flex items-center justify-between mb-6">
    <div class="flex items-center bg-dark-bg rounded-full text-white text-base">
      <button
        id="decrease-btn"
        class="py-3 px-6 bg-transparent"
        @click="decreaseQuantity"
        :disabled="quantity <= minPurchase || isAddToCartProcessing"
      >
        <svg width="14" height="2" viewBox="0 0 14 2" fill="none">
          <path
            d="M1.25 1.75C1.04 1.75 0.86 1.68 0.72 1.53C0.57 1.39 0.5 1.21 0.5 1C0.5 0.79 0.57 0.61 0.72 0.47C0.86 0.32 1.04 0.25 1.25 0.25H12.75C12.96 0.25 13.14 0.32 13.28 0.47C13.43 0.61 13.5 0.79 13.5 1C13.5 1.21 13.43 1.39 13.28 1.53C13.14 1.68 12.96 1.75 12.75 1.75H1.25Z"
            fill="white"
          />
        </svg>
      </button>
      <span id="quantity-display" class="font-bold px-4">{{ quantity }}</span>
      <button
        id="increase-btn"
        class="py-3 px-6 bg-transparent"
        @click="increaseQuantity"
        :disabled="quantity >= maxPurchase || isAddToCartProcessing"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M6.25 7.75H1.25C1.04 7.75 0.86 7.68 0.72 7.53C0.57 7.39 0.5 7.21 0.5 7C0.5 6.79 0.57 6.61 0.72 6.47C0.86 6.32 1.04 6.25 1.25 6.25H6.25V1.25C6.25 1.04 6.32 0.86 6.47 0.72C6.61 0.57 6.79 0.5 7 0.5C7.21 0.5 7.39 0.57 7.53 0.72C7.68 0.86 7.75 1.04 7.75 1.25V6.25H12.75C12.96 6.25 13.14 6.32 13.28 6.47C13.43 6.61 13.5 6.79 13.5 7C13.5 7.21 13.43 7.39 13.28 7.53C13.14 7.68 12.96 7.75 12.75 7.75H7.75V12.75C7.75 12.96 7.68 13.14 7.53 13.28C7.39 13.43 7.21 13.5 7 13.5C6.79 13.5 6.61 13.43 6.47 13.28C6.32 13.14 6.25 12.96 6.25 12.75V7.75Z"
            fill="white"
          />
        </svg>
      </button>
    </div>

    <input
      id="qty"
      v-model="quantity"
      type="number"
      :min="minPurchase"
      :max="maxPurchase"
      :step="product.purchaseSteps || 1"
      class="hidden"
      data-testid="product-quantity"
    />

    <button
      class="btn-default-px-9 btn-default btn-black-bg flex px-6 md:px-[75px] w-full justify-center"
      data-testid="add-to-cart-button"
      :disabled="!isAvailable || isAddToCartProcessing"
      :class="{
        'opacity-50 cursor-not-allowed': !isAvailable || isAddToCartProcessing,
      }"
      @click="addToCartProxy"
    >
      {{ $t("product.bundles.addBundleToCart") }}
      <span class="ml-2">
        <i
          v-if="isAddToCartProcessing"
          class="fa-solid fa-spinner animate-spin"
        ></i>
        <svg
          v-else
          width="16"
          height="20"
          viewBox="0 0 16 20"
          fill="none"
          xmlns="https://www.w3.org/2000/svg"
        >
          <path
            d="M2.3077 19.5C1.80257 19.5 1.375 19.325 1.025 18.975C0.675 18.625 0.5 18.1974 0.5 17.6923V6.3077C0.5 5.80257 0.675 5.375 1.025 5.025C1.375 4.675 1.80257 4.5 2.3077 4.5H4.25V4.25C4.25 3.2141 4.61602 2.33013 5.34808 1.59808C6.08011 0.866026 6.96408 0.5 7.99997 0.5C9.03588 0.5 9.91984 0.866026 10.6519 1.59808C11.3839 2.33013 11.75 3.2141 11.75 4.25V4.5H13.6922C14.1974 4.5 14.625 4.675 14.975 5.025C15.325 5.375 15.5 5.80257 15.5 6.3077V17.6923C15.5 18.1974 15.325 18.625 14.975 18.975C14.625 19.325 14.1974 19.5 13.6922 19.5H2.3077ZM2.3077 18H13.6922C13.7692 18 13.8397 17.9679 13.9038 17.9038C13.9679 17.8397 14 17.7692 14 17.6923V6.3077C14 6.23077 13.9679 6.16024 13.9038 6.09613C13.8397 6.03203 13.7692 5.99998 13.6922 5.99998H11.75V8.25C11.75 8.46282 11.6782 8.64102 11.5346 8.7846C11.391 8.92818 11.2128 8.99998 11 8.99998C10.7872 8.99998 10.609 8.92818 10.4654 8.7846C10.3218 8.64102 10.25 8.46282 10.25 8.25V5.99998H5.74995V8.25C5.74995 8.46282 5.67816 8.64102 5.53457 8.7846C5.39099 8.92818 5.21279 8.99998 4.99997 8.99998C4.78716 8.99998 4.60896 8.92818 4.46537 8.7846C4.32179 8.64102 4.25 8.46282 4.25 8.25V5.99998H2.3077C2.23077 5.99998 2.16024 6.03203 2.09612 6.09613C2.03202 6.16024 1.99997 6.23077 1.99997 6.3077V17.6923C1.99997 17.7692 2.03202 17.8397 2.09612 17.9038C2.16024 17.9679 2.23077 18 2.3077 18ZM5.74995 4.5H10.25V4.25C10.25 3.62307 10.0317 3.09133 9.59517 2.6548C9.15864 2.21825 8.62691 1.99998 7.99997 1.99998C7.37304 1.99998 6.84131 2.21825 6.40477 2.6548C5.96822 3.09133 5.74995 3.62307 5.74995 4.25V4.5Z"
            fill="white"
          />
        </svg>
      </span>
    </button>
  </div>
</template>
